import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { api } from '../lib/api.js'
import { PageHeader } from '../components/PageHeader.js'
import { Section } from '../components/Section.js'
import { Card, CardBody, CardHeader, CardFooter } from '../components/Card.js'
import { Button } from '../components/Button.js'
import { Field, Input } from '../components/Field.js'
import { PhoneField } from '../components/PhoneField.js'
import { Money } from '../components/Money.js'

export const Route = createFileRoute('/_app/onboarding/register')({
  component: RegisterAcademyPage,
})

interface PlanRow {
  id: string
  name: string
  description: string | null
  pricingModel: 'pay_as_you_go' | 'monthly' | 'annual'
  priceInr: number
  studentLimit: number | null
  trialDays: number
  features: Record<string, boolean>
}

const FEATURE_LABELS: Record<string, string> = {
  fee_collection: 'Fee collection',
  attendance: 'Attendance',
  performance: 'Test scores',
  parent_communication: 'Parent communication',
  teacher_accounts: 'Teacher accounts',
  exam_boards: 'Exam boards',
  white_label: 'White-label',
  whatsapp_notifications: 'WhatsApp',
  social_login: 'Social login',
  biometric_sync: 'Biometric sync',
  referrals: 'Referrals',
  email_customisation: 'Custom email',
  reporting: 'Reporting',
}

function RegisterAcademyPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const plansQuery = useQuery({
    queryKey: ['billing', 'plans'],
    queryFn: async () => {
      const result = await api<PlanRow[]>('/api/v1/billing/plans')
      if ('error' in result) throw new Error(result.error.message)
      return result.data
    },
  })

  const [name, setName] = useState('')
  const [slug, setSlug] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [city, setCity] = useState('')
  const [stateName, setStateName] = useState('')
  const [planId, setPlanId] = useState<string | null>(null)
  const [fullName, setFullName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const register = useMutation({
    mutationFn: async () => {
      const result = await api<{ academy: { id: string } }>(
        '/api/v1/academies',
        {
          method: 'POST',
          body: JSON.stringify({
            academy: {
              name,
              slug,
              type: 'coaching_centre',
              email,
              phone: phone || undefined,
              city: city || undefined,
              state: stateName || undefined,
            },
            fullName,
            planId: planId ?? undefined,
          }),
        },
      )
      if ('error' in result) throw new Error(result.error.message)
      return result.data
    },
    onSuccess: async () => {
      // The academy + academy_members row now exist, so /me resolves the new
      // role + academy_id. Invalidate it (and features) so the app shell picks
      // them up; Better Auth keeps the same session, no token refresh needed.
      await queryClient.invalidateQueries({ queryKey: ['me'] })
      await queryClient.invalidateQueries({ queryKey: ['academy-features'] })
      navigate({ to: '/dashboard' })
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Registration failed')
    },
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    register.mutate()
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <PageHeader
        title="Set up your academy"
        description="Tell us about your academy and pick a plan. You can change everything later."
      />

      <form onSubmit={onSubmit} className="space-y-8">
        <Card>
          <CardHeader title="Academy details" description="Shown to your parents and teachers." />
          <CardBody className="space-y-4">
            <Field label="Your full name" required>
              <Input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
            </Field>
            <Field label="Academy name" required>
              <Input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Bright Stars Academy" />
            </Field>
            <Field
              label="Slug"
              required
              hint={
                slug ? (
                  <>
                    <code className="font-mono">{slug}.brightstars.in</code>
                  </>
                ) : (
                  'lowercase letters, digits, hyphens'
                )
              }
            >
              <Input
                value={slug}
                onChange={(e) =>
                  setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))
                }
                required
                pattern="[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?"
                placeholder="bright-stars"
              />
            </Field>
            <Field label="Contact email" required>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="hello@yourdomain.com"
              />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <PhoneField label="Phone" optional value={phone} onChange={setPhone} />
              <Field label="City" optional>
                <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Mumbai" />
              </Field>
            </div>
            <Field label="State" optional>
              <Input value={stateName} onChange={(e) => setStateName(e.target.value)} placeholder="Maharashtra" />
            </Field>
          </CardBody>
        </Card>

        <Section title="Choose a plan" description="Free trial included. Billing starts after the trial.">
          {plansQuery.isLoading && <p className="text-sm text-fg-subtle">Loading plans…</p>}
          {plansQuery.error && (
            <p className="text-sm text-danger">
              {plansQuery.error instanceof Error
                ? plansQuery.error.message
                : 'Failed to load plans'}
            </p>
          )}
          {plansQuery.data && plansQuery.data.length === 0 && (
            <p className="text-sm text-fg-subtle">
              No published plans yet — your account will be created without one.
            </p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {plansQuery.data?.map((plan) => (
              <PlanCard
                key={plan.id}
                plan={plan}
                selected={planId === plan.id}
                onSelect={() => setPlanId(plan.id)}
              />
            ))}
          </div>
        </Section>

        <Card>
          <CardFooter className="flex items-center justify-between">
            <p className="text-xs text-fg-subtle">
              By continuing you agree to our terms and acceptable use policy.
            </p>
            <div className="flex items-center gap-3">
              {error ? <span className="text-sm text-danger">{error}</span> : null}
              <Button type="submit" loading={register.isPending}>
                Create academy
              </Button>
            </div>
          </CardFooter>
        </Card>
      </form>
    </div>
  )
}

function PlanCard({
  plan,
  selected,
  onSelect,
}: {
  plan: PlanRow
  selected: boolean
  onSelect: () => void
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left rounded-lg border bg-surface p-4 transition-all shadow-sm ${
        selected
          ? 'border-[var(--color-primary)] ring-1 ring-[var(--color-primary)]'
          : 'border-border hover:border-border-strong'
      }`}
    >
      <div className="flex items-baseline justify-between">
        <h3 className="font-semibold text-fg">{plan.name}</h3>
        <span className="text-xs uppercase text-fg-subtle">{plan.pricingModel}</span>
      </div>
      <p className="mt-2">
        <Money paise={plan.priceInr} className="text-2xl font-semibold text-fg" />
        <span className="ml-1 text-sm text-fg-subtle">
          {plan.pricingModel === 'monthly'
            ? '/mo'
            : plan.pricingModel === 'annual'
              ? '/yr'
              : ''}
        </span>
      </p>
      {plan.description && (
        <p className="mt-2 text-sm text-fg-muted">{plan.description}</p>
      )}
      <div className="mt-3 pt-3 border-t border-border grid grid-cols-2 gap-x-3 gap-y-1">
        {Object.entries(plan.features)
          .filter(([, v]) => v)
          .slice(0, 6)
          .map(([key]) => (
            <span key={key} className="text-xs text-fg-muted flex items-center gap-1.5">
              <span className="text-success">✓</span>
              {FEATURE_LABELS[key] ?? key}
            </span>
          ))}
      </div>
      <p className="mt-3 text-xs text-fg-subtle">
        {plan.studentLimit ? `Up to ${plan.studentLimit} students` : 'Unlimited students'} ·{' '}
        {plan.trialDays}-day trial
      </p>
    </button>
  )
}
